// Invitations waiting for you.
//
// An invitation used to be reachable only through the link in its email, which
// works once, for the person who still has that email open, in the browser they
// happen to be signed into. Every other path ended nowhere: click it signed
// out and the token was dropped at the sign-in gate; read the mail on a phone
// and open the app on a laptop and the invitation may as well not exist.
//
// This screen is the other half. It asks the database what is open for *this*
// signed-in address (matched on the confirmed email in auth.users, never a
// claim from here) and offers to accept it. Nothing is accepted automatically:
// joining someone's workspace — and, for an agency person, taking on a fixed
// term of access to their audience — is a decision, and the terms are on screen
// when it is made.
//
// TWO KINDS, ONE SCREEN (ADR-0022). A workspace invitation gives you a role in
// somebody's workspace; an organisation invitation puts you on their roster and
// gives you nothing anywhere until they also grant you a role. They are
// genuinely different offers and the copy says so — but to the person receiving
// them they are the same question, "does this person want me to work with them",
// so splitting them across two screens would be modelling our tables rather
// than their day.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { acceptMyInvitation, myInvitations, type PendingInvitation } from "../lib/people";
import {
  acceptOrganisationInvitation, myOrganisationInvitations,
  ORG_ROLE_BLURB, ORG_ROLE_LABEL, type OrganisationInvitation,
} from "../lib/organisation";
import { useWorkspace } from "../lib/WorkspaceContext";
import { logError } from "../lib/logger";
import { dismissInvitation, isDismissed } from "../lib/invitations";

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

export function Invitations() {
  const navigate = useNavigate();
  const { refresh, select } = useWorkspace();
  const [list, setList] = useState<PendingInvitation[] | null>(null);
  const [orgList, setOrgList] = useState<OrganisationInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [joined, setJoined] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Independently: an organisation invitation must still show if the
    // workspace query fails, and the reverse. One broken half should not make
    // the whole screen say "nothing is waiting", which is the one wrong answer
    // this page can give.
    setList(await myInvitations());
    setOrgList(await myOrganisationInvitations().catch(() => []));
  }, []);

  useEffect(() => {
    load().catch((e) => {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "my_invitations" });
    });
  }, [load]);

  async function accept(invitation: PendingInvitation) {
    setBusy(invitation.id);
    setError(null);
    try {
      const result = await acceptMyInvitation(invitation.id);
      setJoined(`You have joined ${result.workspace_name} as ${result.role}.`);
      await refresh();
      select(result.workspace_id);
      // Straight into the workspace they just joined, at the screen their role
      // actually needs (ADR-0016) — not back to a list of one accepted thing.
      setTimeout(() => navigate("/start"), 1200);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "accept_my_invitation" });
      setBusy(null);
    }
  }

  async function acceptOrg(invitation: OrganisationInvitation) {
    setBusy(invitation.id);
    setError(null);
    try {
      const result = await acceptOrganisationInvitation(invitation.id);
      // Deliberately not followed by a redirect. Joining an organisation does
      // not put you anywhere — it puts you on a roster — and sending somebody
      // to a workspace they cannot yet reach would be a lie told by a router.
      setJoined(
        `You are now ${ORG_ROLE_LABEL[result.org_role]} in ${result.organisation_name}. `
        + "Nothing you already had has changed.");
      await refresh();
      await load();
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "accept_organisation_invitation" });
    } finally {
      setBusy(null);
    }
  }

  if (error && list === null) return <div className="card err">{error}</div>;
  if (list === null) return <p className="muted">Looking for invitations…</p>;

  const open = list.filter((i) => !isDismissed(i.id));
  const openOrgs = orgList.filter((i) => !isDismissed(i.id));
  const nothing = open.length === 0 && openOrgs.length === 0;

  return (
    <>
      <h1>Invitations</h1>
      <p className="muted" style={{ marginTop: "-.4rem", maxWidth: "62ch" }}>
        Someone has asked you to work with them. Accepting adds your account to their workspace or
        their organisation; either way it changes nothing about the workspace and the accounts you
        already have — those stay yours.
      </p>

      {error && <div className="card err">{error}</div>}
      {joined && <div className="card ok-note notice">{joined}</div>}

      {openOrgs.length > 0 && (
        <div className="invite-list" style={{ marginBottom: "1rem" }}>
          {openOrgs.map((i) => (
            <div className="card invite-offer" key={i.id}>
              <div>
                <h2>{i.organisation_name}</h2>
                <p className="muted">
                  {i.invited_by_email
                    ? <>{i.invited_by_email} invited you to join </>
                    : <>You were invited to join </>}
                  <strong>{i.organisation_name}</strong> as{" "}
                  <strong>{ORG_ROLE_LABEL[i.org_role]}</strong>.
                </p>

                <ul className="invite-terms muted">
                  {/* The thing people get wrong about an organisation
                      invitation, said before they accept rather than after they
                      wonder why they cannot see anything. */}
                  <li>
                    This puts you on their <strong>roster</strong>. On its own it gives you access
                    to none of their workspaces — they grant that separately, one workspace at a
                    time.
                  </li>
                  <li>{ORG_ROLE_BLURB[i.org_role]}</li>
                  <li>
                    <strong>Everything you already have stays yours.</strong> Your own workspace,
                    your connected accounts and anything you have published are unaffected.
                  </li>
                  <li>The invitation expires on {longDate(i.expires_at)}.</li>
                </ul>
              </div>

              <div className="row invite-offer-actions">
                <button className="primary" disabled={busy !== null}
                        onClick={() => void acceptOrg(i)}>
                  {busy === i.id ? "Joining…" : `Join ${i.organisation_name}`}
                </button>
                <button disabled={busy !== null}
                        onClick={() => { dismissInvitation(i.id); void load(); }}>
                  Not now
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {nothing ? (
        <div className="card muted">
          No invitations are waiting for this email address. If you were expecting one, ask whoever
          invited you to send it again — and check it went to the address you signed in with.
          {" "}<Link to="/start">Go to the app</Link>.
        </div>
      ) : (
        <div className="invite-list">
          {open.map((i) => (
            <div className="card invite-offer" key={i.id}>
              <div>
                <h2>{i.workspace_name}</h2>
                <p className="muted">
                  {i.invited_by_email ? <>{i.invited_by_email} invited you as </> : <>You were invited as </>}
                  <strong>{i.role_name}</strong>
                  {i.scope_type === "connection" && i.scope_name
                    ? <> for one account: <strong>{i.scope_name}</strong>.</>
                    : <> across the workspace.</>}
                </p>

                <ul className="invite-terms muted">
                  {/* An outside collaborator's terms are the ones worth reading
                      twice, so they are stated rather than implied. */}
                  {i.relationship === "external" && (
                    <li>
                      You are being added as someone <strong>outside</strong> their organisation
                      {i.organisation ? <>, on behalf of <strong>{i.organisation}</strong></> : null}.
                    </li>
                  )}
                  {i.access_expires_at && (
                    <li>
                      This access ends on <strong>{longDate(i.access_expires_at)}</strong>. After
                      that you will need a new invitation.
                    </li>
                  )}
                  <li>The invitation itself expires on {longDate(i.expires_at)}.</li>
                </ul>
              </div>

              <div className="row invite-offer-actions">
                <button className="primary" disabled={busy !== null}
                        onClick={() => void accept(i)}>
                  {busy === i.id ? "Joining…" : `Accept and join ${i.workspace_name}`}
                </button>
                {/* Not a decline: the invitation stays open, because refusing on
                    someone else's behalf is not this button's business. It just
                    stops this browser asking again. */}
                <button disabled={busy !== null}
                        onClick={() => { dismissInvitation(i.id); void load(); }}>
                  Not now
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
